import { useState } from 'react';
import { X } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '@/components/ui/sheet';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';

export interface SidebarTab {
  id: string;
  label: string;
  content: React.ReactNode;
}

interface DetailsSidebarProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  displayId: string;
  tabs: SidebarTab[];
  defaultTab?: string;
  footerLabel: string;
  onFooterClick: () => void;
}

const DetailsSidebar = ({
  open,
  onOpenChange,
  displayId,
  tabs,
  defaultTab,
  footerLabel,
  onFooterClick,
}: DetailsSidebarProps) => {
  const [activeTab, setActiveTab] = useState(defaultTab ?? tabs[0]?.id ?? '');

  const gridColsClass =
    tabs.length === 2
      ? 'grid-cols-2'
      : tabs.length === 3
        ? 'grid-cols-3'
        : `grid-cols-${tabs.length}`;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-[600px] sm:max-w-[600px] p-0 gap-0 flex flex-col [&>button]:hidden border-gray-200"
      >
        <SheetHeader className="px-6 py-6 border-b border-gray-200 space-y-0">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <SheetTitle className="font-semibold text-gray-900">
                <span className="font-bold">{displayId.slice(0, 3)}</span>
                <span>{displayId.slice(3)}</span>
              </SheetTitle>
            </div>
            <button
              onClick={() => onOpenChange(false)}
              className="text-gray-400 hover:text-gray-600 cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-auto">
          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className="flex flex-col gap-2 w-full"
          >
            <div className="px-6 pt-4 pb-2">
              <TabsList
                className={`w-full ${gridColsClass} bg-gray-100 h-9`}
                style={{ display: 'grid' }}
              >
                {tabs.map(tab => (
                  <TabsTrigger
                    key={tab.id}
                    value={tab.id}
                    className="flex-1 data-[state=active]:bg-white text-sm"
                  >
                    {tab.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>

            {tabs.map(tab => (
              <TabsContent
                key={tab.id}
                value={tab.id}
                className="flex-1 outline-none px-6 py-6 mt-0"
              >
                {tab.content}
              </TabsContent>
            ))}
          </Tabs>
        </div>

        <SheetFooter className="mt-auto px-6 py-4 border-t border-gray-200 gap-2 sm:justify-end">
          <Button onClick={onFooterClick}>{footerLabel}</Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default DetailsSidebar;
